/**
 * Settings > Time & billing (firm admins): the firm-wide switch, then how
 * time is captured, reviewed and coded for export. Off by default; turning it
 * on lights up the tracker, the timesheet and Billing review for everyone.
 */
import { ArrowUpRight } from "lucide-react";
import { useState, type FC } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { queryClient } from "@/lib/query/queryClient";

import { setTimeTrackingEnabled } from "../../../redux/lawyer/lawyerSlice";
import { ruleActions } from "../actions";
import { TimeAdminAPI } from "../api/timeApi";
import { refreshTime, timeKeys, useFirmTimeSettings } from "../queries";
import { TIME_REVIEW_PATH } from "../routes";
import { RuleList } from "../rules/RuleList";
import type {
  AgentWorkPolicy,
  FirmTimeSettings,
  FirmTimeSettingsPatch
} from "../types";
import { Segmented, SettingGroup, SettingRow } from "./SettingRow";

const ROUNDING = [
  { id: "6", label: "0.1h" },
  { id: "15", label: "0.25h" },
  { id: "1", label: "1 min" }
] as const;

const FEES = [
  { id: "flat_fee", label: "Flat fee" },
  { id: "hourly", label: "Hourly" }
] as const;

const AGENT_WORK: ReadonlyArray<{
  id: AgentWorkPolicy;
  title: string;
  desc: string;
}> = [
  {
    id: "context_only",
    title: "Context only",
    desc: "Agent runs appear as evidence on entries. Only a person's own time is counted. Recommended under ABA Formal Opinion 512."
  },
  {
    id: "suggest_review_time",
    title: "Suggest review time",
    desc: "Also suggests a short entry for reviewing each agent draft, which the timekeeper must confirm."
  }
];

export const idleSecondsFrom = (minutes: string): number | null => {
  const n = Number(minutes);
  if (!Number.isFinite(n) || n < 1 || n > 30) return null;
  return Math.round(n * 60);
};

type Props = { onRequestClose?: () => void };

export const TimeBillingSettings: FC<Props> = ({ onRequestClose }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { data: settings, isLoading, isError } = useFirmTimeSettings();
  const [idle, setIdle] = useState<string | null>(null);
  const [ledes, setLedes] = useState<string | null>(null);

  const save = async (patch: FirmTimeSettingsPatch): Promise<void> => {
    const key = timeKeys.admin("settings");
    const before = queryClient.getQueryData<FirmTimeSettings>(key);
    queryClient.setQueryData<FirmTimeSettings>(key, (old) =>
      old ? { ...old, ...patch } : old
    );
    try {
      const saved = await TimeAdminAPI.saveSettings(patch);
      queryClient.setQueryData(key, saved);
      if (patch.enabled !== undefined) {
        dispatch(setTimeTrackingEnabled(saved.enabled));
        toast(
          saved.enabled
            ? "Time tracking is on for your firm"
            : "Time tracking is off. Entries are kept."
        );
      }
    } catch (error) {
      queryClient.setQueryData(key, before);
      toast.error(
        error instanceof Error ? error.message : "Couldn't save the setting"
      );
    } finally {
      void refreshTime();
    }
  };

  if (isError) {
    return (
      <p className="text-sm text-fg-3">
        Couldn&apos;t load time and billing settings.
      </p>
    );
  }
  if (isLoading || !settings) {
    return (
      <div className="flex flex-col gap-3" data-testid="time-billing-loading">
        <Skeleton className="h-14 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  const openBillingIds = () => {
    onRequestClose?.();
    navigate(`${TIME_REVIEW_PATH}?tab=ids`);
  };

  return (
    <div className="flex flex-col" data-testid="time-billing-settings">
      <div className="flex items-center gap-2">
        <Badge variant="secondary">Firm Admins</Badge>
        <span className="text-sm text-fg-3">
          Applies to everyone at your firm.
        </span>
      </div>

      <div className="mt-4 rounded-xl border border-line-2 px-3.5">
        <SettingRow
          title="Time tracking"
          desc={
            settings.enabled
              ? "CaseBlink drafts each person's time from their work. Each person reviews and releases their own entries."
              : "Off. Turn it on to draft time from work in CaseBlink, email and calendar. Nothing is captured until you do."
          }
        >
          <Switch
            aria-label="Turn on time tracking"
            checked={settings.enabled}
            onCheckedChange={(on) => void save({ enabled: on })}
          />
        </SettingRow>
      </div>

      {settings.enabled ? (
        <>
          <SettingGroup label="Capture">
            <SettingRow
              title="Rounding"
              desc="Each entry rounds up to this increment. 0.1h is the legal billing standard."
            >
              <Segmented
                label="Rounding"
                value={
                  ROUNDING.find(
                    (r) => r.id === String(settings.rounding_minutes)
                  )?.id ?? "6"
                }
                options={ROUNDING}
                onChange={(v) => void save({ rounding_minutes: Number(v) })}
              />
            </SettingRow>
            <SettingRow
              title="Idle after"
              desc={`Minutes without keyboard or mouse input before time stops counting. Reading a PDF allows ${Math.round(
                settings.idle_document_seconds / 60
              )} minutes.`}
            >
              <span className="flex items-center gap-1.5">
                <Input
                  aria-label="Idle minutes"
                  inputMode="numeric"
                  className="h-8 w-16 text-right"
                  value={idle ?? String(Math.round(settings.idle_seconds / 60))}
                  onChange={(e) => setIdle(e.currentTarget.value)}
                  onBlur={() => {
                    if (idle === null) return;
                    const seconds = idleSecondsFrom(idle);
                    setIdle(null);
                    if (seconds === null) {
                      toast.error("Idle is between 1 and 30 minutes");
                      return;
                    }
                    if (seconds !== settings.idle_seconds)
                      void save({ idle_seconds: seconds });
                  }}
                />
                <span className="text-sm text-fg-3">min</span>
              </span>
            </SettingRow>
            <div className="flex flex-col gap-2 border-b border-line-2 py-3.5">
              <span className="text-sm font-medium">Agent work</span>
              <RadioGroup
                aria-label="Agent work"
                value={settings.agent_work}
                onValueChange={(v: string) => {
                  const match = AGENT_WORK.find((o) => o.id === v);
                  if (match) void save({ agent_work: match.id });
                }}
                className="gap-2.5"
              >
                {AGENT_WORK.map((o) => (
                  <div key={o.id} className="flex items-start gap-2.5">
                    <RadioGroupItem
                      id={`agent-work-${o.id}`}
                      value={o.id}
                      className="mt-0.5"
                    />
                    <Label
                      htmlFor={`agent-work-${o.id}`}
                      className="flex flex-col items-start gap-0.5 font-normal"
                    >
                      <span className="text-base font-medium">{o.title}</span>
                      <span className="text-sm text-fg-3">{o.desc}</span>
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            </div>
          </SettingGroup>

          <SettingGroup label="Review">
            <SettingRow
              title="Private until released"
              desc="Admins only see an entry once the timekeeper releases it."
            >
              <Switch
                aria-label="Private until released"
                checked={settings.private_until_released}
                onCheckedChange={(on) =>
                  void save({ private_until_released: on })
                }
              />
            </SettingRow>
            <SettingRow
              title="Require approval before export"
              desc="Released entries need an admin's approval before they can be exported."
            >
              <Switch
                aria-label="Require approval before export"
                checked={settings.require_approval}
                onCheckedChange={(on) => void save({ require_approval: on })}
              />
            </SettingRow>
          </SettingGroup>

          <SettingGroup label="Billing">
            <SettingRow
              title="Default fee arrangement"
              desc="For new cases. Flat-fee time is tracked for profitability and exported as non-billable."
            >
              <Segmented
                label="Default fee arrangement"
                value={
                  settings.default_fee_arrangement === "flat_fee"
                    ? "flat_fee"
                    : "hourly"
                }
                options={FEES}
                onChange={(v) => void save({ default_fee_arrangement: v })}
              />
            </SettingRow>
            <SettingRow
              title="UTBMS task codes"
              desc="Activity codes (A101–A111) are always on. Add task codes if your clients' e-billing requires them."
            >
              <Switch
                aria-label="UTBMS task codes"
                checked={settings.utbms_task_codes}
                onCheckedChange={(on) => void save({ utbms_task_codes: on })}
              />
            </SettingRow>
            <SettingRow
              title="Firm ID for LEDES"
              desc="Usually your federal tax ID."
            >
              <Input
                aria-label="Firm ID for LEDES"
                className="h-8 w-35 font-mono"
                placeholder="47-1234567"
                value={ledes ?? settings.firm_ledes_id ?? ""}
                onChange={(e) => setLedes(e.currentTarget.value)}
                onBlur={() => {
                  if (ledes === null) return;
                  const next = ledes.trim();
                  setLedes(null);
                  if (next !== (settings.firm_ledes_id ?? ""))
                    void save({ firm_ledes_id: next || null });
                }}
              />
            </SettingRow>
            <SettingRow
              title="Rates and billing IDs"
              desc="Timekeeper rates, classifications and matter numbers live in Billing review."
            >
              <Button size="sm" variant="secondary" onClick={openBillingIds}>
                Open Billing IDs
                <ArrowUpRight data-icon="inline-end" />
              </Button>
            </SettingRow>
          </SettingGroup>

          <SettingGroup label="Pre-bill rules">
            <span className="py-2 text-sm leading-4.5 text-fg-3">
              Your firm&apos;s own billing rules, checked on every case before
              approval. Rules from a client&apos;s guidelines live on the
              company&apos;s Billing tab or the client&apos;s profile.
            </span>
            <RuleList
              rules={settings.rules ?? []}
              canEdit
              testId="firm-rules"
              empty="No firm rules yet."
              onChange={(rules, message) =>
                void ruleActions.saveFirmRules(
                  rules,
                  settings.rules ?? [],
                  message
                )
              }
            />
          </SettingGroup>
        </>
      ) : null}
    </div>
  );
};
